import React, { useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { htmlToText } from 'html-to-text';
import { MdOutlineContentCopy, MdOutlineFileDownload } from "react-icons/md";
import { FaCheck } from "react-icons/fa6";
import { IoMdClose } from "react-icons/io";
import { SlRefresh } from "react-icons/sl";
import { Skeleton } from "antd";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { coInvestorAISummary, fetchAISummary, fetchInvestorAISummary, fetchCFSAISummary } from "./useGetProfileDetails";
import { useLocation, useParams } from "react-router";
import useSubmitLog from "./useSumbitLog";
import Constants from "../Constants";
import viLogo from "../images/vi-logo.png";
import './aisummary.css';

if (typeof pdfMake !== "undefined") {
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;
}

const sanitizeFileName = (value) => (value || 'AI_Summary').replace(/[^a-z0-9]+/gi, '_');

const getPdfInlineText = (node, inheritedStyle = {}) => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent?.replace(/\s+/g, ' ') || '';
    return text ? [{ text, ...inheritedStyle }] : [];
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const tagName = node.tagName.toLowerCase();
  const nextStyle = { ...inheritedStyle };

  if (["strong", "b"].includes(tagName)) {
    nextStyle.bold = true;
  }

  if (["em", "i"].includes(tagName)) {
    nextStyle.italics = true;
  }

  if (tagName === "a") {
    nextStyle.color = '#1d4ed8';
    nextStyle.decoration = 'underline';
    const href = node.getAttribute('href');
    if (href) {
      nextStyle.link = href;
    }
  }

  if (tagName === 'br') {
    return [{ text: '\n', ...nextStyle }];
  }

  return Array.from(node.childNodes).flatMap((childNode) => getPdfInlineText(childNode, nextStyle));
};

const convertHtmlToPdfContent = (html) => {
  if (!html) {
    return [];
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = document.body.firstElementChild;

  if (!root) {
    return [];
  }

  const convertBlock = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      return text
        ? [{ text, style: 'body' }]
        : [];
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return [];
    }

    const tagName = node.tagName.toLowerCase();

    if (['ul', 'ol'].includes(tagName)) {
      const items = Array.from(node.children)
        .filter((child) => child.tagName?.toLowerCase() === 'li')
        .map((listItem) => ({
          text: getPdfInlineText(listItem).filter((segment) => segment.text?.trim() || segment.text === '\n'),
        }))
        .filter((item) => item.text.length > 0);

      if (!items.length) {
        return [];
      }

      return [
        {
          [tagName === 'ul' ? 'ul' : 'ol']: items,
          margin: [0, 0, 0, 10],
          style: 'body',
        },
      ];
    }

    if (tagName === 'table') {
      const rows = Array.from(node.querySelectorAll('tr')).map((row) =>
        Array.from(row.children).map((cell) => ({
          text: getPdfInlineText(cell).filter((segment) => segment.text?.trim() || segment.text === '\n'),
          style: row.querySelector('th') ? 'tableHeader' : 'tableCell',
        }))
      );

      if (!rows.length) {
        return [];
      }

      return [
        {
          table: {
            headerRows: rows[0]?.some((cell) => cell.style === 'tableHeader') ? 1 : 0,
            body: rows,
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 12],
        },
      ];
    }

    if (/^h[1-6]$/.test(tagName)) {
      return [
        {
          text: getPdfInlineText(node).filter((segment) => segment.text?.trim() || segment.text === '\n'),
          style: tagName,
        },
      ];
    }

    if (tagName === 'hr') {
      return [
        {
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: '#d1d5db' }],
          margin: [0, 4, 0, 12],
        },
      ];
    }

    if (tagName === 'blockquote') {
      return [
        {
          text: getPdfInlineText(node).filter((segment) => segment.text?.trim() || segment.text === '\n'),
          style: 'blockquote',
        },
      ];
    }

    const children = Array.from(node.childNodes).flatMap((childNode) => convertBlock(childNode));
    const inlineText = getPdfInlineText(node).filter((segment) => segment.text?.trim() || segment.text === '\n');

    if (['p', 'div'].includes(tagName) && inlineText.length) {
      return [
        {
          text: inlineText,
          style: 'body',
        },
      ];
    }

    return children;
  };

  return Array.from(root.childNodes).flatMap((childNode) => convertBlock(childNode));
};

const tableToTsv = (table) => {
  const matrix = [];
  const rows = Array.from(table.querySelectorAll('tr'));

  rows.forEach((row, rowIndex) => {
    if (!matrix[rowIndex]) {
      matrix[rowIndex] = [];
    }

    let colIndex = 0;

    Array.from(row.cells).forEach((cell) => {
      while (matrix[rowIndex][colIndex] !== undefined) {
        colIndex += 1;
      }

      const rowSpan = Math.max(parseInt(cell.getAttribute('rowspan') || '1', 10), 1);
      const colSpan = Math.max(parseInt(cell.getAttribute('colspan') || '1', 10), 1);
      const cellText = (cell.innerText || cell.textContent || '').replace(/\s+/g, ' ').trim();

      for (let r = 0; r < rowSpan; r += 1) {
        if (!matrix[rowIndex + r]) {
          matrix[rowIndex + r] = [];
        }

        for (let c = 0; c < colSpan; c += 1) {
          matrix[rowIndex + r][colIndex + c] = cellText;
        }
      }

      colIndex += colSpan;
    });
  });

  const maxColumns = Math.max(0, ...matrix.map((row) => row.length));

  return matrix
    .map((row) => Array.from({ length: maxColumns }, (_, i) => row[i] ?? '').join('\t'))
    .join('\n');
};

const imageUrlToDataUrl = async (imageUrl) => {
  const response = await fetch(imageUrl);
  const blob = await response.blob();

  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const AISummary = ({ type }) => {
  
  const [aiSummary, setAISummary] = useState("");
  const [name, setName] = useState("");
  const [summaryLoader, setSummaryLoader] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloadLoader, setDownloadLoader] = useState(false);
  const summaryContentRef = useRef(null);

  const { companyId: id, investorId } = useParams();
  const submitLog = useSubmitLog();

  const getAISummaryHandler = async (id) => {
    setAISummary("");
    setSummaryLoader(true);
    
    try {
      let summary;
      if (type === "investor") {
        summary = await fetchInvestorAISummary(investorId || id);
        submitLog("SECA");
      } else if (type === "coinvestor"){
        summary = await coInvestorAISummary(investorId || id);
        submitLog("CIA");
      }else if(type === "CFS"){
        summary = await fetchCFSAISummary(id);
        submitLog("CFSAIS");
      } else {
        summary = await fetchAISummary(id);
        submitLog("AIS");
      }
      if (summary) {
        setAISummary(summary.summary);
        setName(summary.name)
      }
    } catch (error) {
      console.error("Error fetching AI summary:", error);
      setAISummary(error.response.data.summary);
    } finally {
      setSummaryLoader(false);
    }
  };

  useEffect(() => {
      const currentId = type === "investor" ? (investorId || id) : id;
      getAISummaryHandler(currentId);
  }, [id, investorId]);

const copySingleTable = async (table) => {
  const plainTextTable = tableToTsv(table);

  if (!plainTextTable) {
    return;
  }

  if (navigator.clipboard?.write && window.ClipboardItem) {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plainTextTable], { type: 'text/plain' }),
        'text/html': new Blob([table.outerHTML], { type: 'text/html' }),
      }),
    ]);
  } else {
    await navigator.clipboard.writeText(plainTextTable);
  }
};

useEffect(() => {
  if (summaryLoader || !summaryContentRef.current) {
    return;
  }

  const summaryNode = summaryContentRef.current;
  const tables = Array.from(summaryNode.querySelectorAll('table'));
  const cleanupHandlers = [];
  const copyIconMarkup = renderToStaticMarkup(<MdOutlineContentCopy size={14} />);
  const getCopyButtonMarkup = (label = '') => `${copyIconMarkup}<span>${label}</span>`;

  tables.forEach((table) => {
    const wrapperDiv = document.createElement('div');
    wrapperDiv.className = 'ai-summary-table-wrapper flex items-start gap-4';

    const tableCopyButton = document.createElement('button');
    tableCopyButton.type = 'button';
    tableCopyButton.className = 'px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 text-black mt-6 inline-flex items-center';
    tableCopyButton.innerHTML = getCopyButtonMarkup('');
    tableCopyButton.title = 'Copy this table';

    const onTableCopy = async () => {
      try {
        await copySingleTable(table);
        tableCopyButton.innerHTML = getCopyButtonMarkup('Copied');
        setTimeout(() => {
          tableCopyButton.innerHTML = getCopyButtonMarkup('');
        }, 1500);
      } catch (error) {
        console.error('Failed to copy table:', error);
      }
    };

    tableCopyButton.addEventListener('click', onTableCopy);
    
    // Insert wrapper before table, then add button and table into wrapper
    table.parentNode?.insertBefore(wrapperDiv, table);
    wrapperDiv.appendChild(table);
    wrapperDiv.appendChild(tableCopyButton);

    cleanupHandlers.push(() => tableCopyButton.removeEventListener('click', onTableCopy));
  });

  return () => {
    cleanupHandlers.forEach((cleanup) => cleanup());
    summaryNode
      .querySelectorAll('.ai-summary-table-wrapper')
      .forEach((node) => node.remove());
  };
}, [aiSummary, summaryLoader]);

const handleCopy = async () => {
  try {
    const plainText = htmlToText(aiSummary, {
      wordwrap: 130,
      selectors: [
        { selector: 'a', options: { ignoreHref: true } },
        { selector: 'img', format: 'skip' },
      ],
    });

    await navigator.clipboard.writeText(plainText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); // Reset after 2s
  } catch (err) {
    console.error("Failed to copy:", err);
  }
};

const handleDownload = async () => {
  try {
    setDownloadLoader(true);

    const contentBlocks = convertHtmlToPdfContent(aiSummary);
    const watermarkImage = await imageUrlToDataUrl(viLogo);
    const copyrightText = Constants.excelPopupMessage;

    if (!contentBlocks.length) {
      const fallbackText = htmlToText(aiSummary, {
        wordwrap: 100,
        selectors: [
          { selector: 'a', options: { ignoreHref: true } },
          { selector: 'img', format: 'skip' },
        ],
      }).trim();

      if (fallbackText) {
        contentBlocks.push({ text: fallbackText, style: 'body' });
      }
    }

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 50, 50, 80],
      content: [
        {
          text: name || 'AI Summary',
          style: 'title',
        },
        ...(type === 'CFS'
          ? [
              {
                text: 'Analysis of Standalone Financials',
                style: 'subtitle',
              },
            ]
          : []),
        ...contentBlocks,
      ],
      header: (currentPage) => (
        currentPage === 1
          ? {
              image: watermarkImage,
              width: 120,
              alignment: 'right',
              margin: [0, 10, 10, 0],
            }
          : null
      ),
      background: () => ({
        image: watermarkImage,
        width: 260,
        opacity: 0.20,
        absolutePosition: { x: 160, y: 350 },
      }),
      footer: () => ({
        text: copyrightText,
        style: 'copyrightFooter',
        margin: [40, 20, 40, 0],
        alignment: 'left',
      }),
      styles: {
        title: {
          fontSize: 18,
          bold: true,
          color: '#BC8B20',
          margin: [0, 0, 0, 8],
        },
        subtitle: {
          fontSize: 11,
          italics: true,
          color: '#6b7280',
          margin: [0, 0, 0, 16],
        },
        body: {
          fontSize: 10.5,
          lineHeight: 1.4,
          margin: [0, 0, 0, 10],
          alignment: 'justify',
        },
        h1: {
          fontSize: 18,
          bold: true,
          color: '#111827',
          margin: [0, 10, 0, 8],
        },
        h2: {
          fontSize: 16,
          bold: true,
          color: '#111827',
          margin: [0, 10, 0, 8],
        },
        h3: {
          fontSize: 12,
          bold: true,
          color: '#111827',
          margin: [0, 8, 0, 6],
        },
        h4: {
          fontSize: 12.5,
          bold: true,
          color: '#111827',
          margin: [0, 8, 0, 6],
        },
        h5: {
          fontSize: 11.5,
          bold: true,
          color: '#111827',
          margin: [0, 6, 0, 4],
        },
        h6: {
          fontSize: 10.5,
          bold: true,
          color: '#111827',
          margin: [0, 6, 0, 4],
        },
        blockquote: {
          fontSize: 10.5,
          italics: true,
          color: '#4b5563',
          margin: [12, 4, 0, 12],
        },
        tableHeader: {
          bold: true,
          fillColor: '#f3f4f6',
          color: '#111827',
          margin: [0, 4, 0, 4],
        },
        tableCell: {
          margin: [0, 4, 0, 4],
        },
        copyrightFooter: {
          fontSize: 7.5,
          color: '#6b7280',
        },
      },
      defaultStyle: {
        fontSize: 10.5,
      },
    };

    pdfMake.createPdf(docDefinition).download(`${sanitizeFileName(name)}.pdf`);
  } catch (error) {
    console.error('Failed to download PDF:', error);
  } finally {
    setDownloadLoader(false);
  }
};

  return (
    <div className="flex font-sans_book_body cursor-default justify-center items-center z-10 fixed left-0 top-0 w-screen h-screen bg-[#e3e3e3] transition-opacity">
      <div className="mt-24 mb-4 flex flex-col items-center w-full h-[calc(100vh-7rem)] overflow-y-auto">
        {/* Paper document */}
        <div
          className="bg-white w-[95%] md:w-[8.5in] shadow-lg border border-gray-200 relative"
          style={{
            padding: "1rem 1.25rem",
            fontFamily: "'Calibri', 'Segoe UI', sans-serif",
            lineHeight: "1.6",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          }}
        >
          {summaryLoader ? (
            <div className="flex flex-col justify-center items-center py-20">
              <div className="text-gray-500 mb-6 text-center">
                <p className="font-medium">
                  "Analyzing VI data and crafting your summary..."
                </p>
              </div>
              <Skeleton active paragraph={{ rows: 4 }} />
              <div className="mt-4 w-full">
                <Skeleton active paragraph={{ rows: 3 }} />
              </div>
            </div>
          ) : (
            <>
              {/* Document header */}
              <div className="flex justify-between border-b-2 border-[#BC8B20] pb-4 mb-6">
                <div>
                  <h1
                    className="text-2xl font-semibold text-[#BC8B20] mb-1"
                    style={{ fontFamily: "'Cambria', 'Georgia', serif" }}
                  >
                    {name}
                  </h1>
                  {type === "CFS" && (
                    <p className="text-[15px] text-gray-500 italic">
                      Analysis of Standalone Financials
                    </p>
                  )}
                </div>
                <div className="ml-auto">
                  <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded font-semibold">
                    BETA
                  </span>
                </div>
                {/* <p className="text-xs text-gray-400 mt-2">AI Generated Summary • {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p> */}
              </div>

              {/* Document content */}
              <div className="markdown">
                <div
                  ref={summaryContentRef}
                  dangerouslySetInnerHTML={{ __html: aiSummary }}
                  style={{
                    color: "#333",
                    fontSize: "11pt",
                    textAlign: "justify",
                  }}
                />
              </div>
            </>
          )}

          {/* Document footer */}
          {!summaryLoader &&
            aiSummary &&
            aiSummary !==
              "There was an error in generating the company profile" &&
            aiSummary !==
              "There was an error in generating the investor profile" && (
              <div className="mt-12 pt-4 border-t border-gray-200 text-xs text-gray-400 flex justify-between items-center">
                <div className="ml-auto flex items-center gap-2">
                  <button
                    onClick={handleDownload}
                    className="p-2 hover:bg-gray-100 text-black focus:outline-none rounded transition-all flex items-center gap-1"
                    title="Download as PDF"
                    disabled={downloadLoader}
                  >
                    <MdOutlineFileDownload size={16} />
                    <span className="text-xs">{downloadLoader ? 'Downloading' : 'Download'}</span>
                  </button>
                  <button
                    onClick={handleCopy}
                    className="p-2 hover:bg-gray-100 text-black focus:outline-none rounded transition-all flex items-center gap-1"
                    title="Copy to clipboard"
                  >
                    {copied ? (
                      <>
                        <FaCheck size={16} />
                        <span className="text-xs">Copied</span>
                      </>
                    ) : (
                      <>
                        <MdOutlineContentCopy size={16} />
                        <span className="text-xs">Copy</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
        </div>

        {/* Bottom spacing */}
        <div className="h-8 flex-shrink-0"></div>
      </div>
    </div>
  );
};

export default AISummary;